/**
 * useVaultRegistry — fetches the canonical ten-vault registry from
 * the backend (GET /api/vaults/registry).
 *
 * The registry is an UNAUTHENTICATED endpoint — it's safe to call
 * during dashboard skeleton render, before login completes.
 *
 * Returns:
 *   - vaults: array of VaultDefinition (10 entries)
 *   - pilotVaults: array of 6 pilot-ready vaults
 *   - underDevelopmentVaults: array of 4 non-pilot vaults
 *   - byKey: { [key]: VaultDefinition }
 *   - counts: { total, pilot_ready, under_development, experimental }
 *   - loading: bool
 *   - error: Error | null
 *
 * Why: every frontend component that knows about vaults previously
 * shipped its own hard-coded list. They all now subscribe to this
 * single source of truth so a registry change in the backend cascades
 * to every component without code changes.
 */
import { useEffect, useState, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache the registry across hook instances — it doesn't change
// during a session and we don't want N=concurrent requests on
// every dashboard mount.
let _cachedRegistry = null;
let _cachePromise = null;

export function useVaultRegistry() {
  const [state, setState] = useState({
    vaults: _cachedRegistry?.vaults || [],
    counts: _cachedRegistry?.counts || { total: 0, pilot_ready: 0,
                                          under_development: 0,
                                          experimental: 0 },
    loading: !_cachedRegistry,
    error: null,
  });

  useEffect(() => {
    if (_cachedRegistry) return;
    if (!_cachePromise) {
      _cachePromise = axios.get(`${API}/vaults/registry`)
        .then((r) => { _cachedRegistry = r.data; return r.data; })
        .catch((e) => { _cachePromise = null; throw e; });
    }
    let cancelled = false;
    _cachePromise.then((data) => {
      if (cancelled) return;
      setState({ vaults: data.vaults, counts: data.counts, loading: false, error: null });
    }).catch((err) => {
      if (cancelled) return;
      setState((s) => ({ ...s, loading: false, error: err }));
    });
    return () => { cancelled = true; };
  }, []);

  return useMemo(() => {
    const pilotVaults = state.vaults.filter((v) => v.pilot_scope);
    const underDevelopmentVaults = state.vaults.filter((v) => !v.pilot_scope);
    const byKey = state.vaults.reduce((acc, v) => {
      acc[v.key] = v; return acc;
    }, {});
    return { ...state, pilotVaults, underDevelopmentVaults, byKey };
  }, [state]);
}
